// California Polytechnic State University, San Luis Obispo: public college
// page (#2652), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
// #3859 (2026-10-11), page vs KB: the cost table now shows the Science and
// Mathematics, Business and Liberal Arts tuition the KB holds, with the
// nonresident tuition and opportunity fee as one line; the Engineering
// figures are in the note.
//  - Cal Poly's Common Data Set 2025-2026 (Institutional Research PDF,
//    CDS-PDF-2025-2026_final_web_0.pdf): C1 applicants/admits/enrollees for
//    Fall 2025 with the residency table, C8A (no use of SAT or ACT, and "not
//    considered for admission, even if submitted" for Fall 2027), C21/C22 (no
//    early decision or early action). Quirk: the PDF's text layer uses a
//    shifted font for some words, so the checkboxes and labels were read on
//    the rendered pages. The PDF itself is hosted on Amazon S3
//    (content-calpoly-edu.s3.amazonaws.com/ir/1/images/CDS-PDF-2025-2026_final_web_0.pdf);
//    the source link is the Institutional Research CDS page that links to it.
//  - calpoly.edu admissions pages: First-Year Dates and Deadlines, Selection
//    Criteria for First-Year Students, First-Year Student Profile (the 2025
//    preliminary applied and selected counts by college), Impaction. The
//    pages carry no entry year; the filing window matches the CSU's Fall
//    2027 priority period (calstate.edu Application Dates & Deadlines, read
//    for agreement).
//  - calstate.edu Freshman Testing Requirements (the CSU's systemwide test
//    policy).
//  - Cal Poly Financial Aid, Undergraduate Costs of Attendance 2026-27: a new
//    San Luis Obispo student living in a residence hall, in the Colleges of
//    Engineering, Architecture or Agriculture. The CDS G1/G5 figures differ
//    (they use the CDS's own definitions and leave out the nonresident
//    opportunity fee); the financial aid office's budget is used here.
//  - College Scorecard school page, id 110422 (net price by family income;
//    the shared DEMO_KEY was rate-limited, so the figures are the ones the
//    official page loads from the same API).
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Cal Poly Common Data Set 2025-2026',
        url: 'https://ir.calpoly.edu/content/publications_reports/cds/index',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'Cal Poly Admissions, First-Year Dates and Deadlines',
        url: 'https://www.calpoly.edu/admissions/first-year-student/dates-and-deadlines',
        read: '2026-10-01',
    },
    selection: {
        label: 'Cal Poly Admissions, Selection Criteria for First-Year Students',
        url: 'https://www.calpoly.edu/admissions/first-year-student/selection-criteria',
        read: '2026-10-01',
    },
    firstYearProfile: {
        label: 'Cal Poly Admissions, First-Year Student Profile',
        url: 'https://www.calpoly.edu/admissions/first-year-student/selection-criteria/student-profile',
        read: '2026-10-01',
    },
    impaction: {
        label: 'Cal Poly Admissions, Impaction',
        url: 'https://www.calpoly.edu/admissions/impaction',
        read: '2026-10-01',
    },
    csuTests: {
        label: 'California State University, Freshman Testing Requirements',
        url: 'https://www.calstate.edu/apply/freshman/getting_into_the_csu/Pages/testing-requirements.aspx',
        read: '2026-10-01',
    },
    costs: {
        label: 'Cal Poly Financial Aid, Undergraduate Costs of Attendance 2026-27',
        url: 'https://www.calpoly.edu/financial-aid/costs-and-affordability/undergraduate-costs-attendance-2026-2027',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?110422-California-Polytechnic-State-University-San-Luis-Obispo',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'california-polytechnic-state-university-san-luis-obispo',
    kbId: 'california_polytechnic_state_university_san_luis_obispo',
    name: 'California Polytechnic State University, San Luis Obispo',
    shortName: 'Cal Poly SLO',
    aliases: ['SLO', 'Cal Poly San Luis Obispo'],
    location: 'San Luis Obispo, CA',
    control: 'Public',
    lastChecked: '2026-10-11',
    sources: SOURCES,
    // From the KB's sourced branding (Cal Poly color guidelines); decoration only.
    branding: {
        primary: '#154734',
        secondary: '#BD8B13',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/california_polytechnic_state_university_san_luis_obispo_logo.png',
    },

    // The direct answer and short FAQ (#4229): every figure restates one the
    // page already shows, from the source named beside it.
    // The search it answers: california polytechnic state university acceptance rate.
    faq: {
        answer: { text: 'Cal Poly San Luis Obispo admitted 30% of first-year applicants for fall 2025, 21,367 of the 71,309 who applied, according to its Common Data Set 2025-2026.', source: 'cds', where: 'section C1' },
        questions: [
            { q: 'What is the Cal Poly SLO acceptance rate for out-of-state students?', a: 'For fall 2025, Cal Poly admitted 4,413 of 7,109 out-of-state first-year applicants, 62.1%. California residents were admitted at 26.4%, 16,743 of 63,520.', source: 'cds', where: 'section C1, residency table' },
            { q: 'What is the Cal Poly acceptance rate by major?', a: 'Cal Poly reports figures by college, the one your major belongs to, in a preliminary fall 2025 profile. Engineering selected 5,008 of 21,393 applicants (23.4%), Liberal Arts 3,960 of 12,805 (30.9%), and Agriculture, Food and Environmental Sciences 2,922 of 7,038 (41.5%).', source: 'firstYearProfile', where: '2025 Preliminary Newly Selected First-Year Student Profile' },
            { q: 'Does Cal Poly SLO have early decision?', a: 'No. Cal Poly has no early decision or early action plan, so there is no early decision acceptance rate. Its Common Data Set answers no to both.', source: 'cds', where: 'sections C21 and C22' },
            { q: 'When is the Cal Poly application deadline?', a: 'For fall 2027 entry, you apply through Cal State Apply, with your application and fees (or fee waivers) in by 11:59 pm Pacific time on November 30. Late applications are not accepted, and every applicant hears by April 1.', source: 'deadlines' },
            { q: 'Does Cal Poly look at SAT or ACT scores?', a: 'No. Cal Poly\'s Common Data Set answers no to using SAT or ACT scores in admission and marks them not considered, even if submitted, for fall 2027 applicants.', source: 'cds', where: 'section C8A' },
        ],
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 71309,
        admitted: 21367,
        enrolled: 5456,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'California residents', applied: 63520, admitted: 16743 },
            { group: 'Out-of-state', applied: 7109, admitted: 4413 },
            { group: 'International', applied: 680, admitted: 211 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        year: 'Fall 2025',
        published: true,
        note: 'Cal Poly\'s First-Year Student Profile reports applicants and selected students by college for fall 2025. You apply to a major, and the college here is the one your major belongs to. The profile calls its figures preliminary, and its totals (71,171 applied, 20,513 selected) differ from the Common Data Set\'s.',
        head: ['College', 'Applied', 'Selected', 'Rate'],
        rows: [
            { name: 'Agriculture, Food and Environmental Sciences', applied: 7038, admitted: 2922, rate: 41.5 },
            { name: 'Architecture and Environmental Design', applied: 3859, admitted: 1044, rate: 27.1 },
            { name: 'Business', applied: 10800, admitted: 2955, rate: 27.4 },
            { name: 'Engineering', applied: 21393, admitted: 5008, rate: 23.4 },
            { name: 'Liberal Arts', applied: 12805, admitted: 3960, rate: 30.9 },
            { name: 'Science and Mathematics', applied: 15276, admitted: 4624, rate: 30.3 },
        ],
        source: 'firstYearProfile',
        where: '2025 Preliminary Newly Selected First-Year Student Profile',
    },
    earlyRounds: {
        title: 'Early decision and early action',
        note: 'Cal Poly has no early decision or early action plan; the Common Data Set answers no to both. There is one Cal State Apply filing period.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Cal Poly is impacted as a campus and in every major: meeting the CSU minimum does not guarantee admission. You declare a major when you apply and compete with other applicants to that major. Some majors ask for more:',
        rows: [
            { school: 'Art and Design', requirement: 'A portfolio, due January 31.', source: 'selection' },
            { school: 'Music', requirement: 'A supplementary application, due January 31.', source: 'selection' },
        ],
        note: 'Applicants from high schools in San Luis Obispo County, northern Santa Barbara County and southern Monterey County get preference as local applicants. You can name an alternate major, considered if space is available.',
        source: 'impaction',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Application window', value: 'Oct 1 to Nov 30' },
        rows: [
            { plan: 'Cal State Apply', deadline: 'November 30', decision: 'By April 1', binding: false },
        ],
        note: 'You apply through Cal State Apply, with the application and fees (or fee waivers) in by 11:59 pm Pacific time on November 30. Late applications are not accepted. The last day to request a change to your application is January 31, and you accept or decline an offer by May 1.',
        source: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-blind',
        blind: true,
        line: 'The CSU no longer uses the ACT or SAT to decide admission at any of its campuses, so scores are not considered at Cal Poly even if you send them. If you are admitted, they can be one of the measures used to place you in math and writing courses.',
        source: 'csuTests',
        cdsLine: 'The Common Data Set agrees: it answers no to using SAT or ACT scores and marks them not considered for admission, even if submitted, for fall 2027 applicants.',
        cdsWhere: 'section C8A',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees (Science and Mathematics, Business, Liberal Arts)', amounts: [14682, 14682] },
            { item: 'Nonresident tuition and opportunity fee', amounts: [0, 22934] },
            { item: 'Housing (residence hall)', amounts: [12682, 12682] },
            { item: 'Meals', amounts: [7656, 7656] },
            { item: 'Books and materials', amounts: [1160, 1160] },
            { item: 'Miscellaneous costs', amounts: [2586, 2586] },
            { item: 'Transportation', amounts: [1466, 1466] },
            { item: 'Loan fees', amounts: [72, 72] },
        ],
        totals: [40304, 63238],
        note: 'For a new student living in a residence hall. Nonresidents pay $14,130 in tuition (30 units at $471) plus an $8,804 opportunity fee. In the Colleges of Engineering, Architecture and Agriculture, tuition and fees are $15,720, which brings the totals to $41,342 in-state and $64,276 out-of-state.',
        source: 'costs',
        netPrice: {
            average: 18832,
            byIncome: [
                { income: '$0 to $30,000', amount: 10354 },
                { income: '$30,001 to $48,000', amount: 9975 },
                { income: '$48,001 to $75,000', amount: 12476 },
                { income: '$75,001 to $110,000', amount: 18082 },
                { income: 'Over $110,000', amount: 31461 },
            ],
            note: 'What first-year, full-time in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Cal Poly\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
